/**
 * Inline token markup used by the door policy assistant: `[[kind:value]]`
 * inside ordinary sentences, rendered by the app as chips (cat names, times,
 * lock/unlock, sounds, sides of the flap, prey flags). Kinds are defined in
 * the gateway's prompts/door-policy-assistant/ASSISTANT.md.
 */
export type TokenKind = 'cat' | 'time' | 'lock' | 'unlock' | 'sound' | 'where' | 'flag';

export type TokenTextSegment =
    | { type: 'text'; text: string }
    | { type: 'token'; kind: TokenKind | string; value: string };

const TOKEN_PATTERN = /\[\[([a-z]+)(?::([^\]]*))?\]\]/g;

export interface ParseTokenTextOptions {
    /** Hide an unterminated trailing `[[…` while the text is still arriving. */
    streaming?: boolean;
}

export function parseTokenText(text: string, options: ParseTokenTextOptions = {}): TokenTextSegment[] {
    const segments: TokenTextSegment[] = [];
    let source = text;

    if (options.streaming) {
        const openIndex = source.lastIndexOf('[[');
        if (openIndex !== -1 && source.indexOf(']]', openIndex) === -1) {
            source = source.slice(0, openIndex);
        }
    }

    let lastIndex = 0;
    for (const match of source.matchAll(TOKEN_PATTERN)) {
        const index = match.index ?? 0;
        if (index > lastIndex) {
            segments.push({ type: 'text', text: source.slice(lastIndex, index) });
        }
        segments.push({ type: 'token', kind: match[1], value: (match[2] ?? '').trim() });
        lastIndex = index + match[0].length;
    }

    if (lastIndex < source.length) {
        segments.push({ type: 'text', text: source.slice(lastIndex) });
    }

    return segments;
}

/** The markup reduced to plain words, for places that cannot render chips. */
export function stripTokenText(text: string): string {
    return text.replace(TOKEN_PATTERN, (_match, kind: string, value?: string) => {
        if (kind === 'cat') {
            return `#${(value ?? '').slice(-4)}`;
        }
        return value ?? kind;
    });
}
